import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Lock } from "lucide-react";
import { submitLead } from "@/lib/api";
import { useSite, useImages, previewCode } from "@/context/SiteContent";
import { TikTokIcon, LinkedInIcon } from "@/components/site/Scott";

const ease = [0.2, 0.7, 0.2, 1];
const up = (delay) => ({ initial: { opacity: 0, y: 22 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay, ease } });
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const NotifyForm = ({ cs }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!emailOk(email.trim())) return setError("Please enter a valid email address.");
    setError(""); setState("busy");
    try { await submitLead({ type: "notify", email: email.trim() }); setState("done"); }
    catch { setState("idle"); setError("We could not save your email right now. Please try again shortly."); }
  };
  if (state === "done") return <div className="cs-thanks" data-testid="coming-soon-thanks">✓ {cs.thanks}</div>;
  return (
    <form className="cs-form" onSubmit={submit} noValidate data-testid="coming-soon-form">
      <input type="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email address" data-testid="coming-soon-email" />
      <button className="btn btn-gold" type="submit" disabled={state === "busy"} data-testid="coming-soon-submit">{state === "busy" ? "Saving..." : cs.cta} <ArrowRight size={16} /></button>
      <div className="form-error" data-testid="coming-soon-error">{error}</div>
    </form>
  );
};

const PreviewUnlock = () => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const failed = !!previewCode.get();
  const go = (e) => { e.preventDefault(); if (!code.trim()) return; window.location.href = `/?preview=${encodeURIComponent(code.trim())}`; };
  return (
    <div className="cs-preview">
      {!open ? (
        <button type="button" className="cs-preview-link" onClick={() => setOpen(true)} data-testid="coming-soon-preview-link"><Lock size={12} /> Preview site</button>
      ) : (
        <form className="cs-preview-form" onSubmit={go} data-testid="coming-soon-preview-form">
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Access code" autoFocus data-testid="coming-soon-preview-input" />
          <button type="submit" className="btn btn-outline" data-testid="coming-soon-preview-submit">Open</button>
          {failed && <small data-testid="coming-soon-preview-error">That code was not recognised.</small>}
        </form>
      )}
    </div>
  );
};

export default function ComingSoon() {
  const { comingSoon: cs, links } = useSite();
  const images = useImages();
  return (
    <div className="cs-page" data-testid="coming-soon-page">
      <div className="hero-layer hero-gridlines" />
      <div className="cs-orb cs-orb-a" /><div className="cs-orb cs-orb-b" />
      <div className="cs-inner">
        <motion.img className="cs-logo" alt="TransitionWorks" src={images.logoDark} {...up(0)} />
        <motion.div className="hero-kicker" {...up(0.12)} data-testid="coming-soon-kicker"><span className="hero-kicker-dot" />{cs.kicker}</motion.div>
        <motion.h1 {...up(0.24)} data-testid="coming-soon-headline">{cs.headline}</motion.h1>
        <motion.p className="cs-sub" {...up(0.36)}>{cs.subline}</motion.p>
        <motion.div className="cs-grid" {...up(0.48)}>
          <div className="cs-copy">
            <p className="lead">{cs.body}</p>
            <NotifyForm cs={cs} />
            <div className="cs-socials">
              <a href={links.tiktok} target="_blank" rel="noopener noreferrer" data-testid="coming-soon-tiktok"><TikTokIcon /> @campustocorporate1</a>
              <a href={links.linkedin} target="_blank" rel="noopener noreferrer" data-testid="coming-soon-linkedin"><LinkedInIcon /> Scott Tang</a>
            </div>
          </div>
          <motion.div className="cs-cover" animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}>
            <img src={images.cover} alt="From Campus to Corporate eBook cover" />
          </motion.div>
        </motion.div>
        <motion.div className="cs-foot" {...up(0.7)}>
          <span>© 2026 TransitionWorks · UEN 53530089A · Same journey. Brighter tomorrows.</span>
          <PreviewUnlock />
        </motion.div>
      </div>
    </div>
  );
}
